import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { emptyStringToNull, emptyStringToUndefined } from 'app/utils/utils';
import { InputField, SwitchField } from 'app/components/Core';
import { CreateIsniData, UpdateIsniData } from 'types/isni.types';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useAppDispatch } from 'app/store';
import { updateProfileEffect } from 'app/store/Profile/effects';
import {
  CreateIsniValidations,
  IsniDialogProps,
  IsniForm,
  UpdateIsniValidations,
} from './types';
import { getDefaultValues, getIsniValidationSchema } from './utils';

export interface IsniDialogComponentProps extends IsniDialogProps {
  createIsni: (data: CreateIsniData) => Promise<number>;
  updateIsni: (data: UpdateIsniData) => Promise<number>;
  createValidations?: CreateIsniValidations;
  updateValidations?: UpdateIsniValidations;
  fetchingValidations: boolean;
}

export const IsniDialogComponent: React.FC<IsniDialogComponentProps> = ({
  open,
  onClose,
  createIsni,
  updateIsni,
  createValidations,
  updateValidations,
  fetchingValidations,
  isni,
}) => {
  const dispatch = useAppDispatch();
  const defaultValues = useMemo(() => getDefaultValues(isni), [isni]);

  const {
    handleSubmit,
    formState: { isValid, isSubmitting, isDirty, errors },
    reset,
    trigger,
    register,
  } = useForm<IsniForm>({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(
      getIsniValidationSchema({
        createValidations,
        updateValidations,
        isni: Boolean(isni),
      })
    ),
  });

  const onSubmit = handleSubmit(
    async ({ isni: isniNumber, firstName, lastName, isDefault }: IsniForm) => {
      try {
        let status: number;

        if (isni) {
          status = await updateIsni({
            firstName: emptyStringToNull(firstName),
            lastName: emptyStringToNull(lastName),
          });
        } else {
          status = await createIsni({
            isni: isniNumber,
            firstName: emptyStringToUndefined(firstName),
            lastName: emptyStringToUndefined(lastName),
          });
        }
        if (isDefault !== undefined) {
          const value = isDefault ? isniNumber ?? isni?.isni : null;
          await dispatch(
            updateProfileEffect({
              formData: {
                preferredIsni: { title: '', value: value as string },
              },
              options: { hideAlerts: true },
            })
          ).unwrap();
        }
        onClose(status);
      } catch (e) {
        // proceed
      }
    }
  );

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [defaultValues, reset, open]);

  return (
    <Modal isOpen={open} isCentered onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return onSubmit(e);
          }}
          data-testid="isni-form"
        >
          <ModalHeader data-testid="dialog-title" title="Add ISNI Number">
            Add ISNI Number
          </ModalHeader>
          <ModalBody data-testid="dialog-content">
            <Grid
              columnGap={4}
              templateRows="repeat(2, 1fr)"
              templateColumns="repeat(2, 1fr)"
            >
              <GridItem>
                <InputField
                  label="First Name"
                  {...register('firstName', {
                    onChange: () => trigger('lastName'),
                    onBlur: () => trigger('lastName'),
                  })}
                  isInvalid={Boolean(errors.firstName)}
                  errorMessage={errors.firstName?.message}
                />
              </GridItem>
              <GridItem>
                <InputField
                  label="Last Name"
                  {...register('lastName', {
                    onChange: () => trigger('firstName'),
                    onBlur: () => trigger('firstName'),
                  })}
                  isInvalid={Boolean(errors.lastName)}
                  errorMessage={errors.lastName?.message}
                />
              </GridItem>
              <GridItem>
                <InputField
                  label="ISNI Number"
                  {...register('isni')}
                  isDisabled={Boolean(isni)}
                  isInvalid={Boolean(errors.isni)}
                  errorMessage={errors.isni?.message}
                />
              </GridItem>
              <GridItem>
                <SwitchField
                  label="Use as Default ISNI"
                  labelProps={{
                    htmlFor: 'isDefault',
                  }}
                  data-testid="default-label"
                  {...register('isDefault')}
                />
              </GridItem>
            </Grid>
          </ModalBody>
          <ModalFooter data-testid="dialog-actions">
            <Button
              onClick={() => onClose()}
              variant="secondary"
              data-testid="IsniDialog-cancel-button"
            >
              Cancel
            </Button>
            <Button
              ml={2}
              type="submit"
              isDisabled={
                !isValid || isSubmitting || fetchingValidations || !isDirty
              }
              data-testid="IsniDialog-save-button"
            >
              {isni ? 'Save' : 'Add'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
